import * as React from 'react';
import {
  Archive,
  Eye,
  EyeOff,
  GripVertical,
  LockKeyhole,
  Plus,
  RefreshCw,
  Save,
  Settings2,
} from 'lucide-react';
import { ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import {
  addTicketFieldOption,
  archiveTicketFieldOption,
  createTicketField,
  getDependencyMappings,
  getTicketFields,
  reorderTicketFields,
  setDependencyMappings,
  updateTicketField,
  type TicketFieldUpdate,
  type TicketFieldRegistryField,
} from '@/lib/ticket-field-registry-api';
import type { NormalizedTicketFieldOption, TicketFieldRendererType } from '@/lib/ticket-field-registry';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, Input, DropdownSelect, PageHeader, SkeletonText, Textarea, useConfirm } from '@/components/ui';

const PALETTE_TYPES: TicketFieldRendererType[] = [
  'lookup',
  'single_line_text',
  'multi_line_text',
  'checkbox',
  'dropdown',
  'dependent',
  'date',
  'number',
  'decimal',
];

const LOOKUP_SOURCES = ['STATIC', 'AGENTS', 'USERS', 'GROUPS', 'ROLE', 'COUNTRIES', 'CUSTOM'] as const;

type TranslateFn = ReturnType<typeof useI18n>['t'];
type TranslateOrFn = ReturnType<typeof useI18n>['tOr'];

function rendererLabel(tOr: TranslateOrFn, type: string) {
  return tOr(`set.ftype.${type}`, type);
}

function toErrorMessage(t: TranslateFn, error: unknown) {
  return error instanceof ApiError ? error.message : t('set.requestFailed');
}

function levelOptionKey(value: string): string {
  return value.trim().toLowerCase();
}

function draftFor(field: TicketFieldRegistryField): TicketFieldUpdate {
  return {
    label: field.label,
    description: field.description ?? '',
    labelForCustomers: field.labelForCustomers ?? '',
    requiredForAgents: field.requiredForAgents,
    requiredForCustomers: field.requiredForCustomers,
    requiredForClosure: field.requiredForClosure,
    displayedToCustomers: field.displayedToCustomers,
    customersCanEdit: field.customersCanEdit,
    customersCanFilter: field.customersCanFilter,
    isActive: field.isActive,
    dependsOnFieldId: field.dependsOnFieldId ?? null,
    dependsOnFieldKey: field.dependsOnFieldKey ?? null,
  };
}

function BooleanSetting({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-start gap-3 rounded-md border p-3 text-sm ${disabled ? 'bg-surface-hover text-ink-subtle' : 'text-ink-muted'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-line-strong text-brand-700 focus:ring-brand-500"
      />
      <span>{label}</span>
    </label>
  );
}

function DropdownOptionsEditor({ fieldKey, choices, onFieldUpdated }: { fieldKey: string; choices: readonly NormalizedTicketFieldOption[] | null; onFieldUpdated: (field: TicketFieldRegistryField) => void }) {
  const { t } = useI18n();
  const options = React.useMemo(() => choices ?? [], [choices]);
  const [newOption, setNewOption] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  async function addOption() {
    const trimmed = newOption.trim();
    const duplicateLevel = fieldKey === 'cf_level' && options.some((option) => (
      [option.value, option.label].some((candidate) => levelOptionKey(candidate) === levelOptionKey(trimmed))
    ));
    if (!trimmed || duplicateLevel) return;
    setBusy(true);
    try {
      const updated = await addTicketFieldOption(fieldKey, trimmed);
      onFieldUpdated(updated);
      setNewOption('');
    } finally {
      setBusy(false);
    }
  }

  async function removeOption(value: string) {
    setBusy(true);
    try {
      const updated = await archiveTicketFieldOption(fieldKey, value);
      onFieldUpdated(updated);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="max-h-40 space-y-1 overflow-auto">
        {options.filter((o) => o.active).map((opt) => (
          <div key={opt.value} className="flex items-center justify-between rounded border bg-surface-hover px-2 py-1 text-xs">
            <span>{opt.label}</span>
            <button type="button" onClick={() => void removeOption(opt.value)} disabled={busy} className="text-ink-subtle hover:text-danger-600 disabled:opacity-50" aria-label={t('set.tf.removeOption', { label: opt.label })}>&times;</button>
          </div>
        ))}
        {options.filter((o) => o.active).length === 0 && <p className="text-xs text-ink-subtle">{t('set.tf.noOptions')}</p>}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={newOption}
          onChange={(e) => setNewOption(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addOption(); } }}
          placeholder={t('set.tf.addOptionPlaceholder')}
          disabled={busy}
          className="flex-1 rounded border border-line-strong px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <button type="button" onClick={() => void addOption()} disabled={!newOption.trim() || busy} className="rounded bg-brand-600 px-2 py-1 text-xs text-white hover:bg-brand-700 disabled:opacity-50">
          {busy ? '...' : t('set.tf.add')}
        </button>
      </div>
    </div>
  );
}

function DependentFieldConfig({
  field,
  fields,
  onDraftChange,
}: {
  field: TicketFieldRegistryField;
  fields: TicketFieldRegistryField[];
  onDraftChange: (parentFieldId: string | null) => void;
}) {
  const { t } = useI18n();
  const parentField = fields.find((candidate) => candidate.id === field.dependsOnFieldId);
  const parentOptions = React.useMemo(() => parentField?.rawChoices ?? [], [parentField]);
  const childOptions = React.useMemo(() => field.rawChoices ?? [], [field.rawChoices]);
  const [mappings, setMappings] = React.useState<Record<string, string[]>>({});
  const [loadingMappings, setLoadingMappings] = React.useState(false);
  const [mappingError, setMappingError] = React.useState<string | null>(null);
  const [mappingsReady, setMappingsReady] = React.useState(false);
  const [savingMapping, setSavingMapping] = React.useState(false);
  const dependencyId = field.dependencyId;

  React.useEffect(() => {
    let active = true;
    if (!dependencyId) {
      setMappings({});
      setMappingError(null);
      setMappingsReady(true);
      setLoadingMappings(false);
      return () => { active = false; };
    }

    setLoadingMappings(true);
    setMappingError(null);
    setMappingsReady(false);
    void getDependencyMappings(dependencyId)
      .then((rows) => {
        if (!active) return;
        const next = rows.reduce<Record<string, string[]>>((result, mapping) => {
          const current = result[mapping.parentOptionId] ?? [];
          if (!current.includes(mapping.childOptionId)) {
            result[mapping.parentOptionId] = [...current, mapping.childOptionId];
          }
          return result;
        }, {});
        setMappings(next);
        setMappingsReady(true);
      })
      .catch((loadError) => {
        if (active) setMappingError(toErrorMessage(t, loadError));
      })
      .finally(() => {
        if (active) setLoadingMappings(false);
      });

    return () => { active = false; };
  }, [dependencyId, fields, t]);

  async function toggleMapping(parentOptionId: string, childOptionId: string) {
    if (!dependencyId || !mappingsReady) return;
    const current = mappings[parentOptionId] ?? [];
    const next = current.includes(childOptionId)
      ? current.filter((id) => id !== childOptionId)
      : [...current, childOptionId];
    setMappings((previous) => ({ ...previous, [parentOptionId]: next }));
    setSavingMapping(true);
    try {
      await setDependencyMappings(dependencyId, parentOptionId, next);
    } catch {
      setMappings((previous) => ({ ...previous, [parentOptionId]: current }));
    } finally {
      setSavingMapping(false);
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-sm font-medium text-ink-muted">{t('set.tf.dependsOnField')}</label>
        <DropdownSelect
          value={field.dependsOnFieldId ?? ''}
          onChange={(value) => onDraftChange(value || null)}
          options={[
            { value: '', label: t('set.tf.selectParent') },
            ...fields
              .filter((candidate) => candidate.id !== field.id && candidate.isActive !== false && ['dropdown', 'dependent'].includes(candidate.rendererType))
              .map((candidate) => ({ value: candidate.id, label: `${candidate.label} (${candidate.fieldKey})` })),
          ]}
        />
        <p className="mt-1 text-xs text-ink-subtle">{t('set.tf.parentHelp')}</p>
      </div>

      {dependencyId && parentOptions.length > 0 && childOptions.length > 0 && (
        <details className="space-y-2">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            {t('set.tf.optionMappings')} {savingMapping && <span className="text-brand-600">{t('set.tf.savingParen')}</span>}
          </summary>
          {loadingMappings ? (
            <SkeletonText lines={3} className="mt-2" />
          ) : mappingError ? (
            <ErrorState title={t('set.tf.mappingsError')} message={mappingError} className="mt-2" />
          ) : mappingsReady ? (
            <div className="max-h-60 space-y-2 overflow-auto rounded border p-2">
              {parentOptions.map((parentOption) => (
                <details key={parentOption.id} className="rounded border bg-surface-hover p-2">
                  <summary className="cursor-pointer text-xs font-medium text-ink-muted">
                    {parentOption.label}{!parentOption.active && t('set.tf.archivedParen')} → {t('set.tf.mappedCount', { count: (mappings[parentOption.id] ?? []).length })}
                  </summary>
                  <div className="mt-2 space-y-1 pl-2">
                    {childOptions.map((childOption) => (
                      <label key={childOption.id} className="flex items-center gap-2 text-xs text-ink-muted">
                        <input
                          type="checkbox"
                          checked={(mappings[parentOption.id] ?? []).includes(childOption.id)}
                          onChange={() => void toggleMapping(parentOption.id, childOption.id)}
                          disabled={savingMapping || !mappingsReady}
                          className="h-3.5 w-3.5 rounded border-line-strong"
                        />
                        {childOption.label}{!childOption.active && t('set.tf.archivedParen')}
                      </label>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          ) : null}
        </details>
      )}

      {field.dependsOnFieldId && !dependencyId && (
        <p className="text-xs text-warning-700">{t('set.tf.saveFirst')}</p>
      )}
    </div>
  );
}

export function TicketFieldsPage() {
  const { confirm } = useConfirm();
  const { t, tOr } = useI18n();
  const errorMessage = React.useCallback((error: unknown) => toErrorMessage(t, error), [t]);
  const paletteFields = PALETTE_TYPES.map((type) => ({
    type,
    label: rendererLabel(tOr, type),
    description: tOr(`set.ftype.desc.${type}`, ''),
  }));
  const filterOptions = [
    { value: 'all', label: t('set.tf.allTypes') },
    ...paletteFields.map(({ type, label }) => ({ value: type, label })),
  ];
  const [fields, setFields] = React.useState<TicketFieldRegistryField[]>([]);
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<TicketFieldUpdate>({});
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<'all' | TicketFieldRendererType>('all');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savingOrder, setSavingOrder] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [draggingFieldKey, setDraggingFieldKey] = React.useState<string | null>(null);
  const [draggingPaletteType, setDraggingPaletteType] = React.useState<TicketFieldRendererType | null>(null);
  const [draftOptionSourceType, setDraftOptionSourceType] = React.useState<TicketFieldUpdate['optionSourceType'] | null>(null);

  const selectedField = fields.find((field) => field.fieldKey === selectedKey) ?? null;

  const loadFields = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nextFields = await getTicketFields();
      setFields(nextFields);
      setSelectedKey((current) => current && nextFields.some((field) => field.fieldKey === current)
        ? current
        : nextFields[0]?.fieldKey ?? null);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [errorMessage]);

  React.useEffect(() => {
    void loadFields();
  }, [loadFields]);

  React.useEffect(() => {
    setDraft(selectedField ? draftFor(selectedField) : {});
    setDraftOptionSourceType(null);
  }, [selectedKey, fields]);

  const visibleFields = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return fields.filter((field) => {
      const matchesSearch = !query || `${field.label} ${field.fieldKey}`.toLowerCase().includes(query);
      const matchesType = filter === 'all' || field.rendererType === filter;
      // Hide nested/dependent fields unless their parent is currently selected
      if (field.dependsOnFieldKey && field.dependsOnFieldKey !== selectedKey) return false;
      return matchesSearch && matchesType;
    });
  }, [fields, filter, search, selectedKey]);

  function selectField(field: TicketFieldRegistryField) {
    setSelectedKey(field.fieldKey);
    setError(null);
  }

  function updateDraft<K extends keyof TicketFieldUpdate>(key: K, value: TicketFieldUpdate[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function saveField() {
    if (!selectedField) return;
    setSaving(true);
    setError(null);
    try {
      const payload: TicketFieldUpdate = { ...draft };
      if (draftOptionSourceType) payload.optionSourceType = draftOptionSourceType;
      else if (selectedField.rendererType === 'lookup') payload.optionSourceType = selectedField.optionSourceType;
      const updated = await updateTicketField(selectedField.fieldKey, payload);
      setFields((current) => current.map((field) => field.fieldKey === updated.fieldKey ? updated : field));
      setDraft(draftFor(updated));
      setDraftOptionSourceType(null);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setSaving(false);
    }
  }

  async function addField(type: TicketFieldRendererType) {
    setDraggingPaletteType(null); // Clear immediately to prevent duplicate calls from bubbling
    const baseKey = `custom_${type}`;
    let suffix = 1;
    let fieldKey = baseKey;
    while (fields.some((field) => field.fieldKey === fieldKey)) {
      suffix += 1;
      fieldKey = `${baseKey}_${suffix}`;
    }

    setSaving(true);
    setError(null);
    try {
      const created = await createTicketField({
        fieldKey,
        label: t('set.tf.newField', { type: rendererLabel(tOr, type) }),
        rendererType: type,
      });
      setFields((current) => [...current, created]);
      setSelectedKey(created.fieldKey);
    } catch (createError) {
      setError(errorMessage(createError));
    } finally {
      setSaving(false);
      setDraggingPaletteType(null);
    }
  }

  async function reorderFields(sourceKey: string, targetKey: string) {
    if (sourceKey === targetKey) return;
    const sourceIndex = fields.findIndex((field) => field.fieldKey === sourceKey);
    const targetIndex = fields.findIndex((field) => field.fieldKey === targetKey);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const nextFields = [...fields];
    const [movedField] = nextFields.splice(sourceIndex, 1);
    nextFields.splice(targetIndex, 0, movedField);
    setFields(nextFields);
    setSavingOrder(true);
    setError(null);
    try {
      const orderedFields = await reorderTicketFields(nextFields.map((field) => field.fieldKey));
      setFields(orderedFields);
    } catch (reorderError) {
      setError(errorMessage(reorderError));
      await loadFields();
    } finally {
      setSavingOrder(false);
      setDraggingFieldKey(null);
    }
  }

  function handleFieldDrop(event: React.DragEvent, targetKey?: string) {
    event.preventDefault();
    if (draggingPaletteType) {
      void addField(draggingPaletteType);
      return;
    }
    if (draggingFieldKey && targetKey) {
      void reorderFields(draggingFieldKey, targetKey);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        className="mb-0"
        breadcrumb={[{ label: t('set.settings'), to: '/settings' }, { label: t('set.ticketFields') }]}
        title={t('set.ticketFields')}
        description={t('set.tf.pageDescription')}
        actions={(
          <Button variant="outline" onClick={() => void loadFields()} disabled={loading || saving || savingOrder}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('set.refresh')}
          </Button>
        )}
      />

      {error && <ErrorState message={error} onRetry={() => void loadFields()} />}

      <div className="grid gap-6 xl:grid-cols-[15rem_minmax(20rem,1fr)_22rem]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('set.tf.addField')}</CardTitle>
            <p className="text-xs text-ink-subtle">{t('set.tf.addFieldHelp')}</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {paletteFields.map((paletteField) => (
              <div
                key={paletteField.type}
                draggable
                onDragStart={() => setDraggingPaletteType(paletteField.type)}
                onDragEnd={() => setDraggingPaletteType(null)}
                className="rounded-md border bg-surface p-3 text-left transition hover:border-brand-400 hover:shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-ink">{paletteField.label}</p>
                    <p className="mt-1 text-xs text-ink-subtle">{paletteField.description}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('set.tf.addType', { type: paletteField.label })}
                    onClick={() => void addField(paletteField.type)}
                    disabled={saving}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card onDragOver={(event) => event.preventDefault()} onDrop={(event) => handleFieldDrop(event)}>
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">{t('set.tf.registry')}</CardTitle>
              {savingOrder && <span className="text-xs text-ink-subtle">{t('set.tf.savingOrder')}</span>}
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_11rem]">
              <Input
                aria-label={t('set.tf.searchAria')}
                placeholder={t('set.tf.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <DropdownSelect
                aria-label={t('set.tf.filterAria')}
                value={filter}
                onChange={(value) => setFilter(value as 'all' | TicketFieldRendererType)}
                options={filterOptions}
                searchable={false}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <SkeletonText lines={8} />
            ) : visibleFields.length === 0 ? (
              fields.length === 0 ? (
                <EmptyState
                  title={t('set.tf.emptyFirstTitle')}
                  description={t('set.tf.emptyFirstDesc')}
                />
              ) : (
                <EmptyState
                  title={t('set.tf.emptyFilterTitle')}
                  description={t('set.tf.emptyFilterDesc')}
                />
              )
            ) : (
              visibleFields.map((field) => (
                <button
                  type="button"
                  key={field.fieldKey}
                  draggable
                  onDragStart={() => setDraggingFieldKey(field.fieldKey)}
                  onDragEnd={() => setDraggingFieldKey(null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => { event.stopPropagation(); handleFieldDrop(event, field.fieldKey); }}
                  onClick={() => selectField(field)}
                  className={`w-full rounded-md border p-3 text-left transition ${selectedKey === field.fieldKey ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-200' : 'bg-surface hover:border-brand-300'} ${!field.isActive ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium text-ink">{field.label}</span>
                        {field.isDefault && <Badge variant="outline">{t('set.default')}</Badge>}
                        {!field.isActive && <Badge variant="danger">{t('set.tf.archived')}</Badge>}
                        {field.dependsOnFieldKey && <Badge variant="default">{t('set.tf.dependent')}</Badge>}
                      </div>
                      <p className="mt-1 truncate font-mono text-xs text-ink-subtle">{field.fieldKey}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-subtle">
                        <span>{rendererLabel(tOr, field.rendererType)}</span>
                        {field.displayedToCustomers ? <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" aria-hidden="true" />{t('set.tf.customerVisible')}</span> : <span className="inline-flex items-center gap-1"><EyeOff className="h-3.5 w-3.5" aria-hidden="true" />{t('set.tf.internal')}</span>}
                      </div>
                    </div>
                    <Settings2 className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings2 className="h-4 w-4" aria-hidden="true" />
              {t('set.tf.configuration')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedField ? (
              <EmptyState
                icon={Settings2}
                title={t('set.tf.selectTitle')}
                description={t('set.tf.selectDesc')}
              />
            ) : (
              <div className="space-y-4">
                <div className="rounded-md bg-surface-hover p-3 text-xs text-ink-muted">
                  <div className="flex items-center gap-2 font-medium text-ink">
                    {selectedField.isDefault && <LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />}
                    {selectedField.fieldKey}
                  </div>
                  <p className="mt-1">{t('set.tf.externalId', { value: selectedField.externalId })}</p>
                  <p>{t('set.tf.sourceRenderer', { source: selectedField.sourceType, renderer: rendererLabel(tOr, selectedField.rendererType) })}</p>
                  {selectedField.optionSourceType && selectedField.optionSourceType !== 'STATIC' && (
                    <p>{t('set.tf.optionSource', { value: selectedField.optionSourceType })}</p>
                  )}
                  {selectedField.dependsOnFieldKey && <p>{t('set.tf.dependsOnKey', { value: selectedField.dependsOnFieldKey })}</p>}
                </div>

                <Input label={t('set.tf.internalLabel')} value={draft.label ?? ''} onChange={(event) => updateDraft('label', event.target.value)} />
                <Input label={t('set.tf.customerLabel')} value={draft.labelForCustomers ?? ''} onChange={(event) => updateDraft('labelForCustomers', event.target.value)} />
                <Textarea
                  id="field-description"
                  label={t('set.description')}
                  rows={3}
                  value={draft.description ?? ''}
                  onChange={(event) => updateDraft('description', event.target.value)}
                />

                {selectedField.rendererType === 'lookup' && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-ink-muted">{t('set.relationshipSource')}</label>
                    <DropdownSelect
                      value={draftOptionSourceType ?? selectedField.optionSourceType ?? 'STATIC'}
                      onChange={(value) => setDraftOptionSourceType(value as TicketFieldUpdate['optionSourceType'])}
                      searchable={false}
                      options={LOOKUP_SOURCES.map((source) => ({ value: source, label: t(`set.src.${source}`) }))}
                    />
                    <p className="mt-1 text-xs text-ink-subtle">{t('set.tf.relationshipHelp')}</p>
                  </div>
                )}

                {selectedField.rendererType === 'dependent' && (
                  <DependentFieldConfig
                    field={selectedField}
                    fields={fields}
                    onDraftChange={(parentFieldId) => {
                      const parentField = fields.find((field) => field.id === parentFieldId);
                      setFields((current) => current.map((field) => field.fieldKey === selectedField.fieldKey
                        ? {
                          ...field,
                          dependsOnFieldId: parentFieldId,
                          dependsOnFieldKey: parentField?.fieldKey ?? null,
                          dependencyId: null,
                        }
                        : field));
                      setDraft((current) => ({
                        ...current,
                        dependsOnFieldId: parentFieldId,
                        dependsOnFieldKey: parentField?.fieldKey ?? null,
                      }));
                    }}
                  />
                )}

                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{t('set.tf.validationVisibility')}</p>
                  <BooleanSetting label={t('set.tf.requiredAgents')} checked={draft.requiredForAgents ?? false} onChange={(value) => updateDraft('requiredForAgents', value)} />
                  <BooleanSetting label={t('set.tf.requiredCustomers')} checked={draft.requiredForCustomers ?? false} onChange={(value) => updateDraft('requiredForCustomers', value)} />
                  <BooleanSetting label={t('set.tf.requiredClosure')} checked={draft.requiredForClosure ?? false} onChange={(value) => updateDraft('requiredForClosure', value)} />
                  <BooleanSetting label={t('set.tf.displayedCustomers')} checked={draft.displayedToCustomers ?? false} onChange={(value) => updateDraft('displayedToCustomers', value)} />
                  <BooleanSetting label={t('set.tf.customersEdit')} checked={draft.customersCanEdit ?? false} onChange={(value) => updateDraft('customersCanEdit', value)} />
                  <BooleanSetting label={t('set.tf.customersFilter')} checked={draft.customersCanFilter ?? false} onChange={(value) => updateDraft('customersCanFilter', value)} />
                  <BooleanSetting label={t('set.tf.active')} checked={draft.isActive ?? false} disabled={selectedField.isDefault} onChange={(value) => updateDraft('isActive', value)} />
                </div>

                {(['dropdown', 'dependent', 'multiselect', 'radio'] as string[]).includes(selectedField.rendererType) && (
                  <details className="space-y-2" open>
                    <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-ink-subtle">{t('set.tf.dropdownOptions', { count: selectedField.rawChoices?.filter((option) => option.active).length ?? 0 })}</summary>
                    <div className="mt-2">
                      <DropdownOptionsEditor
                        fieldKey={selectedField.fieldKey}
                        choices={selectedField.rawChoices}
                        onFieldUpdated={(updated) => {
                          setFields((current) => current.map((f) => f.fieldKey === updated.fieldKey ? updated : f));
                        }}
                      />
                    </div>
                  </details>
                )}

                <Button className="w-full" onClick={() => void saveField()} disabled={saving || savingOrder}>
                  <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                  {saving ? t('set.saving') : t('set.saveChanges')}
                </Button>
                {!selectedField.isDefault && (
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={async () => {
                      if (!await confirm({ title: t('set.tf.archiveTitle', { name: selectedField.label }), description: t('set.tf.archiveDesc'), confirmLabel: t('set.tf.archiveField') })) return;
                      setSaving(true);
                      try {
                        await updateTicketField(selectedField.fieldKey, { isActive: false });
                        await loadFields();
                      } catch (e) {
                        setError(errorMessage(e));
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving || !selectedField.isActive}
                  >
                    <Archive className="mr-2 h-4 w-4" aria-hidden="true" />
                    {selectedField.isActive ? t('set.tf.archiveField') : t('set.tf.alreadyArchived')}
                  </Button>
                )}
                {!selectedField.isDefault && !selectedField.isActive && (
                  <p className="flex items-center gap-1 text-xs text-ink-subtle"><Archive className="h-3.5 w-3.5" aria-hidden="true" />{t('set.tf.archivedNote')}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
